import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.js';

export const TIER_LEVELS = {
  STANDARD: {
    name: 'Standard',
    level: 0,
    color: '#6B7280', // Gray
    requirements: {
      spend: 0,
      referrals: 0
    },
    benefits: {
      streamQuality: '720p',
      customEmotes: 0,
      subRevenue: 0.5, // 50% revenue share
      adRevenue: 0.5
    }
  },
  SILVER: {
    name: 'Silver',
    level: 1,
    color: '#C0C0C0',
    requirements: {
      spend: 100, // $100 USD
      referrals: 25
    },
    benefits: {
      streamQuality: '1080p',
      customEmotes: 5,
      subRevenue: 0.6, // 60% revenue share
      adRevenue: 0.6
    }
  },
  GOLD: {
    name: 'Gold',
    level: 2,
    color: '#FFD700',
    requirements: {
      spend: 500, // $500 USD
      referrals: 50
    },
    benefits: {
      streamQuality: '1440p',
      customEmotes: 10,
      subRevenue: 0.7, // 70% revenue share
      adRevenue: 0.7
    }
  },
  PLATINUM: {
    name: 'Platinum',
    level: 3,
    color: '#E5E4E2',
    requirements: {
      spend: 3000, // $3000 USD
      referrals: 100
    },
    benefits: {
      streamQuality: '4K',
      customEmotes: 20,
      subRevenue: 0.8, // 80% revenue share
      adRevenue: 0.8
    }
  },
  DIAMOND: {
    name: 'Diamond',
    level: 4,
    color: '#B9F2FF',
    requirements: {
      spend: 20000, // $20000 USD
      referrals: 200
    },
    benefits: {
      streamQuality: '4K',
      customEmotes: 50,
      subRevenue: 0.85, // 85% revenue share
      adRevenue: 0.85
    }
  }
};

// Calculate user's eligible tier based on spend and referrals
export const calculateEligibleTier = (totalSpend, totalReferrals) => {
  if (totalSpend >= TIER_LEVELS.DIAMOND.requirements.spend || totalReferrals >= TIER_LEVELS.DIAMOND.requirements.referrals) {
    return TIER_LEVELS.DIAMOND;
  } else if (totalSpend >= TIER_LEVELS.PLATINUM.requirements.spend || totalReferrals >= TIER_LEVELS.PLATINUM.requirements.referrals) {
    return TIER_LEVELS.PLATINUM;
  } else if (totalSpend >= TIER_LEVELS.GOLD.requirements.spend || totalReferrals >= TIER_LEVELS.GOLD.requirements.referrals) {
    return TIER_LEVELS.GOLD;
  } else if (totalSpend >= TIER_LEVELS.SILVER.requirements.spend || totalReferrals >= TIER_LEVELS.SILVER.requirements.referrals) {
    return TIER_LEVELS.SILVER;
  }
  return TIER_LEVELS.STANDARD;
};

// Check and update user's tier if eligible
export const checkAndUpdateTier = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const currentTier = userData.tier || TIER_LEVELS.STANDARD.name;
    const totalSpend = userData.totalSpend || 0;
    const totalReferrals = userData.totalReferrals || 0;

    const eligibleTier = calculateEligibleTier(totalSpend, totalReferrals);

    // Only update if eligible for a higher tier
    if (TIER_LEVELS[eligibleTier.name].level > TIER_LEVELS[currentTier].level) {
      await updateDoc(userRef, {
        tier: eligibleTier.name,
        tierUpdatedAt: new Date().toISOString(),
        benefits: eligibleTier.benefits
      });

      return {
        success: true,
        previousTier: currentTier,
        newTier: eligibleTier.name,
        benefits: eligibleTier.benefits
      };
    }

    return {
      success: false,
      message: 'No tier upgrade available',
      currentTier
    };
  } catch (error) {
    console.error('Error updating user tier:', error);
    throw error;
  }
};

// Get tier progress information
export const getTierProgress = (totalSpend, totalReferrals, currentTier) => {
  const tierLevel = TIER_LEVELS[currentTier].level;
  const nextTier = Object.values(TIER_LEVELS).find(tier => tier.level === tierLevel + 1);

  if (!nextTier) {
    return {
      isMaxTier: true,
      spendProgress: 100,
      referralProgress: 100,
      nextTier: null
    };
  }

  const spendProgress = Math.min((totalSpend / nextTier.requirements.spend) * 100, 100);
  const referralProgress = Math.min((totalReferrals / nextTier.requirements.referrals) * 100, 100);

  return {
    isMaxTier: false,
    spendProgress,
    referralProgress,
    nextTier: nextTier.name,
    spendRequired: nextTier.requirements.spend - totalSpend,
    referralsRequired: nextTier.requirements.referrals - totalReferrals
  };
}; 